/**
 * NocturneMoon — one realistic Moon texture (the near side's real seas, bump-mapped craters, Tycho's rays,
 * gibbous phase) rendered once
 * with WebGL into a 2D canvas, shared by the eyepiece simulator and the scroll "dive".
 * Falls back to a hand-drawn 2D Moon when WebGL is unavailable. Textures are cached per size.
 */
(() => {
  if (window.NocturneMoon) return;

  const TAU = Math.PI * 2;
  const rand = (seed) => () => ((seed = (seed * 16807) % 2147483647) - 1) / 2147483646;

  // Disk coordinates (x east, y north, radius 1) of the near side's main seas and young craters,
  // so the Moon reads as the one people know rather than a generic cratered ball.
  const MOON_FRAG = `
precision highp float;
uniform float u_size;
uniform vec3 u_light;
const vec3 OFFSET = vec3(0.3, 0.1, 0.0);
const vec2 TYCHO = vec2(-0.12, -0.68), COPERNICUS = vec2(-0.33, 0.17), KEPLER = vec2(-0.6, 0.14), ARISTARCHUS = vec2(-0.7, 0.38);
vec3 hash33(vec3 p){ p = fract(p * vec3(0.1031, 0.1030, 0.0973)); p += dot(p, p.yxz + 33.33); return fract((p.xxy + p.yxx) * p.zyx); }
float noise3(vec3 p){
  vec3 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(mix(hash33(i).x, hash33(i + vec3(1,0,0)).x, f.x), mix(hash33(i + vec3(0,1,0)).x, hash33(i + vec3(1,1,0)).x, f.x), f.y),
             mix(mix(hash33(i + vec3(0,0,1)).x, hash33(i + vec3(1,0,1)).x, f.x), mix(hash33(i + vec3(0,1,1)).x, hash33(i + vec3(1,1,1)).x, f.x), f.y), f.z);
}
float fbm3(vec3 p){ float v = 0.0, a = 0.5; for (int i = 0; i < 6; i++){ v += a * noise3(p); p *= 2.02; a *= 0.5; } return v; }
// One octave of craters (a share "density" of cells holds one): smooth bowl, raised rim, small central peak.
float craters(vec3 p, float density){
  vec3 i = floor(p), f = fract(p); float res = 0.0;
  for (int x = -1; x <= 1; x++) for (int y = -1; y <= 1; y++) for (int z = -1; z <= 1; z++){
    vec3 g = vec3(float(x), float(y), float(z));
    vec3 h = hash33(i + g);
    if (h.z > density) continue;
    float k = length(g + h - f) / (0.12 + 0.3 * h.x * h.x);
    if (k > 1.6) continue;
    float c = k < 1.0 ? k * k - 1.0 : 0.0;
    c += 0.42 * exp(-(k - 1.0) * (k - 1.0) * 22.0) + 0.18 * h.x * exp(-k * k * 40.0);
    res += c * (0.45 + 0.55 * h.y);
  }
  return res;
}
float named(vec2 d, vec2 c, float r){
  float k = length(d - c) / r;
  return (k < 1.0 ? k * k - 1.0 : 0.0) + 0.5 * exp(-(k - 1.0) * (k - 1.0) * 18.0) + 0.3 * exp(-k * k * 30.0);
}
float sea(vec2 d, vec2 c, vec2 r, float w){ return smoothstep(1.05, 0.6, length((d - c) / (r * 1.22)) + w); }
float maria(vec2 d, vec3 p){
  // Warp the disk so the coastlines come out ragged instead of elliptical.
  d += (vec2(fbm3(p * 2.6), fbm3(p * 2.6 + 5.2)) - 0.5) * 0.2;
  float w = (fbm3(p * 7.0) - 0.5) * 0.55;
  float m = sea(d, vec2(-0.62, 0.25), vec2(0.22, 0.3), w);        // Oceanus Procellarum
  m = max(m, sea(d, vec2(-0.55, -0.1), vec2(0.2, 0.25), w));
  m = max(m, sea(d, vec2(-0.72, 0.0), vec2(0.15, 0.3), w));
  m = max(m, sea(d, vec2(-0.38, 0.1), vec2(0.12, 0.08), w));      // Insularum
  m = max(m, sea(d, vec2(-0.28, 0.45), vec2(0.27, 0.2), w));      // Imbrium
  m = max(m, sea(d, vec2(0.2, 0.43), vec2(0.14, 0.13), w));       // Serenitatis
  m = max(m, sea(d, vec2(0.38, 0.15), vec2(0.19, 0.15), w));      // Tranquillitatis
  m = max(m, sea(d, vec2(0.3, 0.05), vec2(0.12, 0.1), w));
  m = max(m, sea(d, vec2(0.74, 0.28), vec2(0.09, 0.11), w));      // Crisium
  m = max(m, sea(d, vec2(0.62, -0.1), vec2(0.1, 0.17), w));       // Fecunditatis
  m = max(m, sea(d, vec2(0.43, -0.3), vec2(0.08, 0.09), w));      // Nectaris
  m = max(m, sea(d, vec2(-0.2, -0.36), vec2(0.17, 0.12), w));     // Nubium
  m = max(m, sea(d, vec2(-0.33, -0.18), vec2(0.1, 0.08), w));     // Cognitum
  m = max(m, sea(d, vec2(-0.48, -0.42), vec2(0.08, 0.08), w));    // Humorum
  m = max(m, sea(d, vec2(-0.05, 0.74), vec2(0.42, 0.07), w));     // Frigoris
  m = max(m, sea(d, vec2(0.05, 0.25), vec2(0.08, 0.07), w));      // Vaporum
  return m;
}
// Bright ray systems of young craters, fading with distance.
float rays(vec2 d, vec2 c, float r, float reach, float s){
  vec2 v = d - c; float dist = length(v) / r; vec2 dir = v / max(length(v), 1e-4);
  float streak = smoothstep(0.6, 0.95, noise3(vec3(dir * 7.0, s))) + 0.6 * smoothstep(0.68, 0.98, noise3(vec3(dir * 19.0, s + 4.0)));
  return streak * exp(-dist / reach) * smoothstep(1.0, 1.6, dist) + 0.8 * exp(-dist * dist / 3.0);
}
float height(vec3 p, float mar){
  vec2 d = p.xy - OFFSET.xy;
  float rough = 1.0 - 0.75 * mar; // the seas are lava plains, far smoother than the highlands
  return craters(p * 2.4, 0.18) * 0.5
       + craters(p * 5.5 + 3.0, 0.45) * 0.2 * (0.4 + 0.6 * rough)
       + craters(p * 12.0 + 9.0, 0.6) * 0.075 * rough
       + craters(p * 27.0 + 17.0, 0.7) * 0.024 * rough
       + fbm3(p * 14.0) * 0.1 * (0.5 + 0.5 * rough)
       + (named(d, TYCHO, 0.04) + named(d, COPERNICUS, 0.045)) * 0.05 + named(d, KEPLER, 0.022) * 0.025;
}
void main(){
  vec2 d = (gl_FragCoord.xy / u_size) * 2.0 - 1.0;
  float r2 = dot(d, d);
  if (r2 > 1.0) { gl_FragColor = vec4(0.0); return; }
  vec3 n = vec3(d, sqrt(1.0 - r2));
  vec3 p = n + OFFSET;
  float mar = maria(d, p);
  // Bump-mapped normal from the height field.
  float e = 0.003;
  float h0 = height(p, mar);
  vec3 grad = vec3(height(p + vec3(e,0,0), mar) - h0, height(p + vec3(0,e,0), mar) - h0, height(p + vec3(0,0,e), mar) - h0) / e;
  vec3 bn = normalize(n - (grad - dot(grad, n) * n) * 0.04);
  vec3 seaTone = mix(vec3(0.45, 0.46, 0.49), vec3(0.56, 0.54, 0.5), fbm3(p * 4.0)) * (0.85 + 0.3 * fbm3(p * 3.0 + 2.0));
  vec3 albedo = mix(vec3(0.88, 0.86, 0.81), seaTone, mar);
  albedo *= (0.8 + 0.32 * fbm3(p * 6.0)) * (0.93 + 0.14 * noise3(p * 46.0));
  float ray = rays(d, TYCHO, 0.04, 9.0, 1.0) + 0.6 * rays(d, COPERNICUS, 0.045, 5.0, 7.0) + 0.4 * rays(d, KEPLER, 0.022, 5.0, 13.0);
  ray += 0.9 * exp(-dot(d - ARISTARCHUS, d - ARISTARCHUS) / 0.0003);
  albedo += vec3(0.92, 0.91, 0.88) * min(ray, 1.2) * 0.3;
  vec3 L = normalize(u_light);
  float ndl = dot(bn, L);
  float lit = smoothstep(-0.03, 0.12, dot(n, L));
  // Lommel-Seeliger: the Moon looks evenly bright, not like a shaded ball.
  float mu0 = max(ndl, 0.0), mu = max(n.z, 0.15);
  vec3 v = albedo * (mu0 / (mu0 + mu) * 1.6 * lit + 0.012);
  vec3 col = v / (1.0 + 0.45 * v) * 1.38; // soft shoulder so the sunlit limb doesn't burn out
  float edge = smoothstep(1.0, 0.995, sqrt(r2));
  gl_FragColor = vec4(col * edge, edge);
}`;

  // Sun direction for the dive and the eyepiece: a waxing gibbous Moon, the shadow line on the left.
  const GIBBOUS = [0.62, 0.12, 0.42];

  /* Renders the moon once with WebGL into a 2D canvas. Returns null when WebGL is unavailable. */
  function moonTextureGL(size, light = GIBBOUS) {
    const gc = document.createElement('canvas');
    gc.width = gc.height = size;
    const gl = gc.getContext('webgl', { premultipliedAlpha: false, preserveDrawingBuffer: true });
    if (!gl) return null;
    const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null; };
    const vs = sh(gl.VERTEX_SHADER, 'attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}');
    const fs = sh(gl.FRAGMENT_SHADER, MOON_FRAG);
    if (!vs || !fs) return null;
    const prog = gl.createProgram();
    gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
    gl.useProgram(prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'a');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    gl.uniform1f(gl.getUniformLocation(prog, 'u_size'), size);
    gl.uniform3f(gl.getUniformLocation(prog, 'u_light'), light[0], light[1], light[2]);
    gl.viewport(0, 0, size, size);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    const out = document.createElement('canvas');
    out.width = out.height = size;
    out.getContext('2d').drawImage(gc, 0, 0);
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return out;
  }

  /* 2D fallback moon for browsers without WebGL. */
  function moonTexture(size) {
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const g = c.getContext('2d');
    const r = size / 2;
    const rnd = rand(7);
    g.save();
    g.beginPath();
    g.arc(r, r, r, 0, TAU);
    g.clip();
    g.fillStyle = '#d6d0c2';
    g.fillRect(0, 0, size, size);
    // Fine speckle so the highlands aren't flat.
    for (let i = 0; i < 2400; i++) {
      g.fillStyle = rnd() < 0.5 ? 'rgba(255,252,240,.08)' : 'rgba(60,58,54,.08)';
      const s = 1 + rnd() * 3;
      g.fillRect(rnd() * size, rnd() * size, s, s);
    }
    // Maria: clusters of overlapping soft blobs along a rough band, like the real near side.
    const seas = [[0.38, 0.32, 0.2], [0.55, 0.42, 0.24], [0.66, 0.6, 0.17], [0.42, 0.62, 0.15], [0.3, 0.5, 0.13], [0.72, 0.3, 0.1]];
    for (const [sx, sy, ss] of seas) {
      for (let k = 0; k < 9; k++) {
        const x = (sx + (rnd() - 0.5) * ss) * size, y = (sy + (rnd() - 0.5) * ss) * size, s = ss * size * (0.35 + rnd() * 0.4);
        const grad = g.createRadialGradient(x, y, 0, x, y, s);
        grad.addColorStop(0, 'rgba(92,90,88,.32)');
        grad.addColorStop(0.7, 'rgba(92,90,88,.18)');
        grad.addColorStop(1, 'rgba(92,90,88,0)');
        g.fillStyle = grad;
        g.fillRect(x - s, y - s, s * 2, s * 2);
      }
    }
    // Craters: mostly small, a few large; shadowed on the sun-facing wall, lit on the far wall.
    for (let i = 0; i < 420; i++) {
      const x = rnd() * size, y = rnd() * size, s = Math.pow(rnd(), 5) * r * 0.14 + 1.2;
      const a = 0.1 + rnd() * 0.2;
      g.fillStyle = `rgba(70,66,60,${a})`;
      g.beginPath(); g.arc(x, y, s, 0, TAU); g.fill();
      g.strokeStyle = `rgba(40,38,35,${a + 0.1})`;
      g.lineWidth = Math.max(0.8, s * 0.25);
      g.beginPath(); g.arc(x, y, s * 0.9, Math.PI * 0.75, Math.PI * 1.35); g.stroke();
      g.strokeStyle = `rgba(255,250,236,${a + 0.12})`;
      g.beginPath(); g.arc(x, y, s, -Math.PI * 0.35, Math.PI * 0.3); g.stroke();
      if (s > r * 0.05 && rnd() < 0.5) {
        // Bright ray system around a few young craters.
        g.strokeStyle = 'rgba(255,252,240,.07)';
        g.lineWidth = 2;
        for (let k = 0; k < 12; k++) {
          const ang = rnd() * TAU, len = s * (3 + rnd() * 6);
          g.beginPath(); g.moveTo(x, y); g.lineTo(x + Math.cos(ang) * len, y + Math.sin(ang) * len); g.stroke();
        }
      }
    }
    // Phase: waxing gibbous. The terminator is half an ellipse, softened by stacking slightly shifted fills.
    for (let k = 0; k < 10; k++) {
      const shift = (k - 5) * size * 0.006;
      g.fillStyle = 'rgba(5,7,12,.16)';
      g.beginPath();
      g.arc(r, r, r + 2, Math.PI * 0.5, Math.PI * 1.5);
      g.ellipse(r, r, r * 0.42 + shift, r + 2, 0, -Math.PI * 0.5, Math.PI * 0.5, true);
      g.fill();
    }
    // Limb darkening.
    const limb = g.createRadialGradient(r, r, r * 0.6, r, r, r);
    limb.addColorStop(0, 'rgba(0,0,0,0)');
    limb.addColorStop(1, 'rgba(0,0,0,.25)');
    g.fillStyle = limb;
    g.fillRect(0, 0, size, size);
    g.restore();
    return c;
  }

  const cache = new Map();
  window.NocturneMoon = {
    texture(size) {
      if (!cache.has(size)) cache.set(size, moonTextureGL(size) || moonTexture(size));
      return cache.get(size);
    },
    /* Tonight's Moon: frac is the synodic fraction (0 new, 0.5 full). Null without WebGL, so callers
       can draw a simple vector phase instead of the fixed-phase 2D fallback. */
    phase(size, frac) {
      const key = `${size}:${frac.toFixed(3)}`;
      if (!cache.has(key)) {
        const a = frac * TAU;
        cache.set(key, moonTextureGL(size, [Math.sin(a), 0, -Math.cos(a)]));
      }
      return cache.get(key);
    },
  };
})();
